import { useEffect } from 'react';
import {
  CONSUMER_CMS_SHOPPING_CART,
  COOKIE_CART,
  errorMock,
  IGenericErrorCMS,
  IPersonalInfoCmsMobile,
  IPortabilityMobileCMS,
  IShoppingCartResponse,
  useCmsConfig,
  useDeleteShoppingCartMutation,
} from '@vfit/consumer/data-access';
import {
  API,
  getClickToCallWithKeyOrUrl,
  getFromLocalStorageByKey,
  setItemCookie,
  trackView,
} from '@vfit/shared/data-access';
import {
  errorManager,
  ErrorService,
  ITrackError,
  LoggerInstance,
  toastManager,
} from '@vfit/shared/data-access';
import { organizeSimTypeCard } from '../../components/SimTypeSelectionCard/simTypeSelectionCard.utils';
import { ISimTypeCardData } from '../../components/SimTypeSelectionCard/simTypeSelection.models';
import { IUseCheckoutErrors } from './useCheckoutErrors.models';
import { getTrackError } from '../../checkout.utils';
import { useCheckout } from '../../../iBuyMobile.context';
import { ENABLE_DEBUG } from '../../../iBuyMobile.utils';
import { useQueryClient } from 'react-query';
import { ICMSApiError, ICMSError } from '@vfit/shared/models';

export const useCheckoutErrors = ({
  handleClose,
  replaceBillingAddress,
  createBillingAddress,
}: IUseCheckoutErrors) => {
  const {
    product,
    currentStepKey,
    customerDeliveryBaseInfo,
    campaign,
    cartAsyncInfo,
    customerAsyncInfo,
    customerFlow,
    deliveryAdvance,
    authorizationAsyncInfo,
    checkoutErrors,
    isModalButtonSticky,
    setCheckoutErrors,
  } = useCheckout();
  const queryClient = useQueryClient();
  const {
    mutate: deleteCartMutation,
    isSuccess: isSuccessDeleteCart,
    isLoading: isLoadingDeleteCart,
    isError: isErrorDeleteCart,
  } = useDeleteShoppingCartMutation();
  const personalDataMobileCMS = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_PERSONAL_DATA_MOBILE,
  ) as IPersonalInfoCmsMobile;
  const errorPortability = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_PORTABILITY_MOBILE,
  ) as IPortabilityMobileCMS;
  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR_MOBILE,
  ) as IGenericErrorCMS;
  const organizedDataForSim = organizeSimTypeCard(
    personalDataMobileCMS,
    errorPortability,
  ) as ISimTypeCardData;

  const { errorcomponent: errorComponentCustomer } =
    (useCmsConfig(CONSUMER_CMS_SHOPPING_CART, API.CMS_GET_CUSTOMER_ERROR_MOBILE) as ICMSApiError) ||
    {};
  const customerErrors = errorComponentCustomer?.errors || [];

  const errors: ICMSError[] = [...customerErrors];

  const showAndTrackError = (
    _genericError: IGenericErrorCMS,
    _handleClose?: () => void,
    error?: ITrackError,
    title?: string,
    message?: string,
    actionEvent?: () => void,
    actionText?: string,
    secondActionEvent?: () => void,
    secondActionText?: string,
    isButtonSticky = false,
    disableDeleteCart = false,
    disableTrack = false,
    invertButtons = false,
    icon?: string,
  ) => {
    const firstActionTextIncludeCTC = actionText?.toLowerCase()?.includes('richiamiamo');
    const secondActionTextIncludeCTC = secondActionText?.toLowerCase()?.includes('richiamiamo');
    const firstButtonText =
      product?.removeCTC && firstActionTextIncludeCTC
        ? undefined
        : actionText || _genericError?.genericerror?.buttonLabel || '';
    const secondButtonText =
      product?.removeCTC && secondActionTextIncludeCTC ? undefined : secondActionText;

    const trackErrorRes = error
      ? getTrackError(error, currentStepKey?.toLowerCase(), queryClient)
      : undefined;

    const deleteCartClose = () => {
      _handleClose?.();
      window.location.reload();
    };

    const STATUS_DELETE_CART = ['500', '403', '404'];
    const actualCart: IShoppingCartResponse = getFromLocalStorageByKey('shoppingCart');
    const cartId = actualCart?.id;
    const ENABLE_DELETE_CART =
      (!disableDeleteCart &&
        cartId &&
        error?.error?.status &&
        STATUS_DELETE_CART.includes(error?.error?.status)) ||
      error?.error?.status === 'timeout';
    if (ENABLE_DELETE_CART && !isSuccessDeleteCart && !isLoadingDeleteCart && !isErrorDeleteCart) {
      deleteCartMutation({
        shoppingCartId: cartId,
        customOptions: {
          headers: {
            Authorization: sessionStorage.getItem('uxfauthorization') || '',
          },
        },
      });
    }

    if (trackErrorRes) {
      if (ENABLE_DEBUG) {
        toastManager.handleToast({
          title: trackErrorRes.page_error || 'no error found',
        });
      }
      LoggerInstance.log('Track', trackErrorRes);
      trackView(trackErrorRes);
    }
    errorManager.handleError(ErrorService.getSeverityErrorHigh(), {
      title: title || _genericError?.genericerror?.title || '',
      message: message || _genericError?.genericerror?.description || '',
      onClose: ENABLE_DELETE_CART ? deleteCartClose : _handleClose,
      actionEvent: ENABLE_DELETE_CART ? deleteCartClose : actionEvent || _handleClose,
      actionText: firstButtonText,
      disableTrack: !!trackErrorRes || disableTrack,
      secondActionEvent,
      secondButtonText,
      isButtonSticky,
      invertButtons,
      ...(icon && { icon }),
      ...(trackErrorRes && {
        trackError: {
          ...trackErrorRes,
        },
      }),
      opts: {
        product_name: product?.slug || '',
      },
    });
  };

  // region ERRORS

  const getCtcConfig = (foundedError: ICMSError | undefined, error?: ITrackError) => {
    if (foundedError?.isClickToCall) {
      return {
        title: foundedError?.title || genericError?.genericerror?.title,
        message: foundedError?.message || genericError?.genericerror?.description,
        secondActionEvent: () =>
          getClickToCallWithKeyOrUrl(
            queryClient,
            foundedError?.keyClickToCall || product?.genericErrorCallMeNow?.url || '',
            product,
            handleClose,
            currentStepKey,
            error,
          ),
        secondActionText: foundedError?.button?.title || product?.genericErrorCallMeNow?.label,
        isButtonSticky: isModalButtonSticky,
      };
    }

    return {
      title: genericError?.genericerror?.title,
      message: genericError?.genericerror?.description,
      secondActionEvent: () => {},
      secondActionText: '',
      isButtonSticky: false,
    };
  };

  useEffect(() => {
    if (customerDeliveryBaseInfo?.modifySimDevice.isError) {
      LoggerInstance.log('Error:modifysimdevice_error');
      checkoutErrors?.showAndTrackError?.(
        genericError,
        handleClose,
        { ...customerDeliveryBaseInfo?.modifySimDevice?.error, isBlocking: true },
        organizedDataForSim?.genericsError?.title,
        organizedDataForSim?.genericsError?.description,
        handleClose,
        organizedDataForSim.genericsError.buttonLabel,
      );
    }
  }, [customerDeliveryBaseInfo?.modifySimDevice]);

  useEffect(() => {
    if (customerDeliveryBaseInfo?.deliveryBase.isError) {
      LoggerInstance.log('Error:deliverybase_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...customerDeliveryBaseInfo?.deliveryBase?.error,
        isBlocking: true,
      });
    }
  }, [customerDeliveryBaseInfo?.deliveryBase]);

  useEffect(() => {
    if (customerFlow?.billingAccount.isError) {
      LoggerInstance.log('Error: billingaccount_error');
      const getErrorCode = () => customerFlow?.billingAccount?.error?.errorCode?.toString();
      const foundedError = errors?.find((el) => el?.errorCode === getErrorCode());
      const ctcConfig = getCtcConfig(foundedError, customerFlow?.billingAccount?.error);

      checkoutErrors?.showAndTrackError?.(
        genericError,
        handleClose,
        {
          ...customerFlow?.billingAccount?.error,
          isBlocking: true,
        },
        ctcConfig?.title,
        ctcConfig?.message,
        undefined,
        '',
        ctcConfig?.secondActionEvent,
        ctcConfig?.secondActionText,
        ctcConfig?.isButtonSticky,
      );
    }
  }, [customerFlow?.billingAccount]);

  useEffect(() => {
    if (customerFlow?.checkPrepaidSim?.isError) {
      LoggerInstance.log('Error: checkPrepaidSim_error');
      const getErrorCode = () =>
        customerFlow?.checkPrepaidSim?.error?.errorCode?.toString() || 'checkPrepaidSim';
      const foundedError = errors?.find((el) => el?.errorCode === getErrorCode());
      const ctcConfig = getCtcConfig(foundedError, customerFlow?.checkPrepaidSim?.error);

      checkoutErrors?.showAndTrackError?.(
        genericError,
        handleClose,
        {
          ...customerFlow?.checkPrepaidSim?.error,
          isBlocking: true,
        },
        ctcConfig?.title,
        ctcConfig?.message,
        undefined,
        '',
        ctcConfig?.secondActionEvent,
        ctcConfig?.secondActionText,
        ctcConfig?.isButtonSticky,
      );
    }
  }, [customerFlow?.checkPrepaidSim]);

  useEffect(() => {
    if (customerFlow?.associateCustomer?.isError) {
      LoggerInstance.log('Error: associateCustomer_error');

      const getErrorCode = () => customerFlow?.associateCustomer?.error?.errorCode?.toString();
      const foundedError = errors?.find((el) => el?.errorCode === getErrorCode());
      const ctcConfig = getCtcConfig(foundedError, customerFlow?.associateCustomer?.error);

      checkoutErrors?.showAndTrackError?.(
        genericError,
        handleClose,
        { ...customerFlow?.associateCustomer?.error, isBlocking: true },
        ctcConfig?.title,
        ctcConfig?.message,
        undefined,
        '',
        ctcConfig?.secondActionEvent,
        ctcConfig?.secondActionText,
        ctcConfig?.isButtonSticky,
      );
    }
  }, [customerFlow?.associateCustomer]);

  useEffect(() => {
    if (customerAsyncInfo?.termsAndConditions.isError) {
      LoggerInstance.log('Error: termsandcondition_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...customerAsyncInfo?.termsAndConditions?.error,
        isBlocking: true,
      });
    }
  }, [customerAsyncInfo?.termsAndConditions]);

  useEffect(() => {
    if (customerAsyncInfo?.retrieveMultilines.isError) {
      LoggerInstance.log('Error: retrieveMultilines_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...customerAsyncInfo?.retrieveMultilines?.error,
        isBlocking: true,
      });
    }
  }, [customerAsyncInfo?.retrieveMultilines]);

  useEffect(() => {
    if (customerFlow?.payment?.isError) {
      const getErrorCode = () => customerFlow?.payment?.error?.errorCode?.toString();
      const foundedError = errors?.find((el) => el?.errorCode === getErrorCode());
      const ctcConfig = getCtcConfig(foundedError, customerFlow?.payment?.error);

      LoggerInstance.log('Error: payment_error');
      checkoutErrors?.showAndTrackError?.(
        genericError,
        handleClose,
        {
          ...customerFlow?.payment?.error,
          isBlocking: true,
        },
        ctcConfig?.title,
        ctcConfig?.message,
        undefined,
        '',
        ctcConfig?.secondActionEvent,
        ctcConfig?.secondActionText,
        ctcConfig?.isButtonSticky,
      );
    }
  }, [customerFlow?.payment, customerFlow?.payment?.isError]);

  useEffect(() => {
    if (customerFlow?.paymean?.isError) {
      const getErrorCode = () => customerFlow?.paymean?.error?.errorCode?.toString();
      const foundedError = errors?.find((el) => el?.errorCode === getErrorCode());
      const ctcConfig = getCtcConfig(foundedError, customerFlow?.paymean?.error);

      LoggerInstance.log('Error: payment_error');
      checkoutErrors?.showAndTrackError?.(
        genericError,
        handleClose,
        {
          ...customerFlow?.paymean?.error,
          isBlocking: true,
        },
        ctcConfig?.title,
        ctcConfig?.message,
        undefined,
        '',
        ctcConfig?.secondActionEvent,
        ctcConfig?.secondActionText,
        ctcConfig?.isButtonSticky,
      );
    }
  }, [customerFlow?.paymean, customerFlow?.paymean?.isError]);

  useEffect(() => {
    if (authorizationAsyncInfo?.payMean?.isError) {
      LoggerInstance.log('Error: paymean_error');
      const getErrorCode = () => authorizationAsyncInfo?.payMean?.error?.errorCode?.toString();
      const foundedError = errors?.find((el) => el?.errorCode === getErrorCode());
      const ctcConfig = getCtcConfig(foundedError, authorizationAsyncInfo?.payMean?.error);

      checkoutErrors?.showAndTrackError?.(
        {
          ...genericError,
          genericerror: {
            ...genericError.genericerror,
            title: 'Ops!',
            description:
              'Si è verificato un errore durante il recupero dei pagamenti. Riprovare in seguito',
          },
        },
        handleClose,
        {
          ...authorizationAsyncInfo?.payMean?.error,
          isBlocking: true,
        },
        ctcConfig?.title,
        ctcConfig?.message,
        undefined,
        '',
        ctcConfig?.secondActionEvent,
        ctcConfig?.secondActionText,
        ctcConfig?.isButtonSticky,
      );
    }
  }, [authorizationAsyncInfo?.payMean]);

  useEffect(() => {
    if (authorizationAsyncInfo?.authorization?.isError) {
      LoggerInstance.log('Error: authorization_error');
      const getErrorCode = () =>
        authorizationAsyncInfo?.authorization?.error?.errorCode?.toString();
      const foundedError = errors?.find((el) => el?.errorCode === getErrorCode());
      const ctcConfig = getCtcConfig(foundedError, authorizationAsyncInfo?.authorization?.error);
      LoggerInstance.log(
        'Error: authorization_error CODE: ',
        authorizationAsyncInfo?.authorization?.error?.errorCode?.toString(),
      );

      checkoutErrors?.showAndTrackError?.(
        {
          ...genericError,
          genericerror: {
            ...genericError.genericerror,
            title: 'Ops!',
            description:
              'Si è verificato un errore durante il recupero dei pagamenti. Riprovare in seguito',
          },
        },
        handleClose,
        {
          ...authorizationAsyncInfo?.authorization?.error,
          isBlocking: true,
        },
        ctcConfig?.title,
        ctcConfig?.message,
        undefined,
        '',
        ctcConfig?.secondActionEvent,
        ctcConfig?.secondActionText,
        ctcConfig?.isButtonSticky,
      );
    }
  }, [authorizationAsyncInfo?.authorization]);

  useEffect(() => {
    if (deliveryAdvance?.isError) {
      LoggerInstance.log('Error: deliveryadvance_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...deliveryAdvance?.error,
        isBlocking: true,
      });
    }
  }, [deliveryAdvance]);

  useEffect(() => {
    if (cartAsyncInfo?.simTypeSelection?.isError) {
      LoggerInstance.log('Error: simtypeselection_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...cartAsyncInfo?.simTypeSelection?.error,
        isBlocking: true,
      });
    }
  }, [cartAsyncInfo]);

  useEffect(() => {
    if (campaign?.updateCart?.isError) {
      LoggerInstance.log('Error: campaign_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...campaign?.updateCart?.error,
        isBlocking: true,
      });
    }
  }, [campaign]);

  useEffect(() => {
    if (replaceBillingAddress?.isError) {
      LoggerInstance.log('Error: replacebillingaddress_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...errorMock('replacebilling', {
          url: 'replacebilling',
          status: '500',
          statusText: '500',
        }),
        isBlocking: true,
      });
    }
  }, [replaceBillingAddress]);

  useEffect(() => {
    if (createBillingAddress?.isError) {
      LoggerInstance.log('Error: createbilling_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...errorMock('createbilling', {
          url: 'createbilling',
          status: '500',
          statusText: '500',
        }),
        isBlocking: true,
      });
    }
  }, [createBillingAddress]);

  // endregion

  // if is success delete cart from popup, remove cookie for delete cart on reload
  useEffect(() => {
    if (isSuccessDeleteCart) setItemCookie(COOKIE_CART, '', 0, false);
  }, [isSuccessDeleteCart]);

  useEffect(() => {
    setCheckoutErrors({
      ...checkoutErrors,
      showAndTrackError,
    });
  }, [currentStepKey]);

  return null;
};

